import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  computeLicenseState,
  getLicenseStatus,
  saveLicenseStatus,
} from "@/lib/license";
import { toast } from "sonner";

const LicenseContext = createContext(null);

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicense must be used within a LicenseProvider");
  }
  return context;
};

const notifyLicenseStatus = (licenseStatus) => {
  if (licenseStatus.isExpired) {
    toast.error(
      <span>
        Licenza scaduta, esegui <a href="/subscription" className="underline font-semibold">qui</a> il rinnovo.
      </span>,
    );
    return;
  }

  if (licenseStatus.isOverLimit) {
    console.log("License is over limit", licenseStatus);
    toast.warning(
      "Raggiunto numero massimo pneumatici, puoi eseguire l'upgrade del pacchetto per avere gli pneumatici illimitati.",
    );
  }
};

export const LicenseProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [licenseStatus, setLicenseStatus] = useState(null);

  const refreshLicenseStatus = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setLicenseStatus(null);
      return null;
    }

    try {
      const [licenseResponse, statisticsResponse] = await Promise.all([
        api.get("/user/license"),
        api.get("/statistics"),
      ]);
      console.log("License response:", licenseResponse);
      const nextStatus = computeLicenseState({
        expireLicenses: licenseResponse.data.expire_licenses
          ?? licenseResponse.data.expireLicenses
          ?? licenseResponse.data.ExpireLicenses,
        maxTyreNo: licenseResponse.data.max_tyre_no
          ?? licenseResponse.data.maxTyreNo
          ?? licenseResponse.data.MaxTyreNo,
        totalInStock: statisticsResponse.data.total_in_stock,
      });

      saveLicenseStatus(user.id, nextStatus);
      setLicenseStatus(nextStatus);
      notifyLicenseStatus(nextStatus);
      return nextStatus;
    } catch (error) {
      const nextStatus = computeLicenseState();
      saveLicenseStatus(user.id, nextStatus);
      setLicenseStatus(nextStatus);
      notifyLicenseStatus(nextStatus);
      return nextStatus;
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setLicenseStatus(null);
      return;
    }

    setLicenseStatus(getLicenseStatus(user.id));
    refreshLicenseStatus();
  }, [isAuthenticated, refreshLicenseStatus, user?.id]);

  return (
    <LicenseContext.Provider
      value={{
        licenseStatus,
        isReadOnly: licenseStatus?.isExpired === true,
        isOverLimit: licenseStatus?.isOverLimit === true,
        refreshLicenseStatus,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
};
